import {
  COVERAGE_TYPE,
  type H3CustomOption
} from "@/components/spatialAnalysis/spatialOutputAnalysis/data";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import {
  GEOHASH_AREA_SIZE,
  GEOHASH_RESOLUTION_LABEL,
  H3_AREA_SIZE,
  H3_RESOLUTION_META,
  MAX_GRID_CELL_COUNT
} from "@/constants/spatialResolution";
import { GeohashResolution, H3Resolution } from "@/types/analysisHistory";
import {
  BuildCoverageTypePayloadArgs,
  BuildGridOptionsArgs,
  CoverageConfigType,
  EOutputCoverageType
} from "@/types/spatial";

import { getHexagonAreaAvg } from "h3-js";

// ------------------ GEOHASH RESOLUTIONS LEVEL ---------------------//
const GEOHASH_RESOLUTIONS = Object.values(GeohashResolution).filter(
  Number.isInteger
) as GeohashResolution[];

// ------------------ H3 RESOLUTIONS LEVEL ---------------------//
const H3_RESOLUTIONS = Object.values(H3Resolution).filter(
  Number.isInteger
) as H3Resolution[];

/**
 * Estimates how many H3 cells of `resolution` fit in `areaSize` (m²). Frontend
 * port of the backend `CountEstimateGridResult` (`estimatedCount = ceil(area /
 * avgGridArea)`). The divisor is H3's own global average cell area for the
 * resolution (`getHexagonAreaAvg`) rather than a hardcoded table — the backend
 * samples real cell areas at the geometry's location, and without a geometry
 * here this is the closest area-only approximation. Returns 0 when the divisor
 * is zero (the backend guards this as an error).
 */
export const estimateH3GridCount = (
  areaSize: number,
  resolution: H3Resolution
): number => {
  const avgGridArea = getHexagonAreaAvg(resolution, "m2");
  if (avgGridArea === 0) return 0;

  return Math.ceil(areaSize / avgGridArea);
};

/**
 * Estimates how many geohash cells of `resolution` fit in `areaSize` (m²).
 * Geohash counterpart of `estimateH3GridCount` (`estimatedCount = ceil(area /
 * cellArea)`). Geohash cells have a fixed area per resolution, so the divisor
 * comes from the `GEOHASH_AREA_SIZE` table. Returns 0 when the divisor is zero.
 */
export const estimateGeohashGridCount = (
  areaSize: number,
  resolution: GeohashResolution
): number => {
  const cellArea = GEOHASH_AREA_SIZE[resolution];
  if (cellArea === 0) return 0;

  return Math.ceil(areaSize / cellArea);
};

//------------------ GEOHASH SELECT OPTIONS ---------------------//
export const buildGeohashOptions = ({
  areaSize,
  maxGridCellCount = MAX_GRID_CELL_COUNT,
  isNewEstimation
}: BuildGridOptionsArgs): IOptionItem[] =>
  GEOHASH_RESOLUTIONS.map((resolution) => {
    // New: ceil-based cell-count estimate. Legacy: raw area/cellArea ratio.
    const disabled = isNewEstimation
      ? estimateGeohashGridCount(areaSize, resolution) >= maxGridCellCount
      : areaSize / GEOHASH_AREA_SIZE[resolution] >= maxGridCellCount;
    return {
      value: String(resolution),
      label: `${resolution} ${GEOHASH_RESOLUTION_LABEL[resolution]}`,
      disabled
    };
  });

//------------------ H3 SELECT OPTIONS ---------------------//
export const buildH3Options = ({
  areaSize,
  maxGridCellCount = MAX_GRID_CELL_COUNT,
  isNewEstimation
}: BuildGridOptionsArgs): H3CustomOption[] =>
  H3_RESOLUTIONS.map((resolution) => ({
    value: String(resolution),
    label: `${H3_RESOLUTION_META[resolution].areaKm2} Km2`,
    radius: `${H3_RESOLUTION_META[resolution].radiusKm} Km`,
    // New: ceil-based cell-count estimate. Legacy: raw area/cellArea ratio.
    disabled: isNewEstimation
      ? estimateH3GridCount(areaSize, resolution) >= maxGridCellCount
      : areaSize / H3_AREA_SIZE[resolution] >= maxGridCellCount
  }));

//------------------ COVERAGE TYPE PAYLOAD ---------------------//
/**
 * The coverage-type fragment merged into an analysis `grid_config`. Mirrors the
 * optional `output_mode` / `custom_catchment` fields on `GridConfig`
 * (see `@/types/dataset`).
 */

/**
 * Builds the coverage-type payload for an analysis `grid_config`.
 * Returns `null` for the Standard coverage type (or when the feature is
 * disabled) so the caller can spread it safely:
 *   grid_config: {
 *     grid_type,
 *     level,
 *     ...buildCoverageTypePayload({ isEnabled, outputMode, radius, type })
 *   }
 */
export const buildCoverageTypePayload = ({
  isEnabled,
  outputMode = EOutputCoverageType.STANDARD,
  radius = 0,
  type = ""
}: BuildCoverageTypePayloadArgs): CoverageConfigType | null => {
  if (!isEnabled || outputMode !== EOutputCoverageType.CUSTOM_CATCHMENT) {
    return null;
  }
  return {
    output_mode: EOutputCoverageType.CUSTOM_CATCHMENT,
    custom_catchment: {
      type: type,
      radius_m: radius
    }
  };
};

// Lookup option by coverage value, e.g. COVERAGE_TYPE_BY_VALUE["STANDARD"].
export const COVERAGE_TYPE_BY_VALUE = Object.fromEntries(
  COVERAGE_TYPE.map((option) => [option.value, option])
) as Record<EOutputCoverageType, IOptionItem>;
